import { BidDocument } from '@util/firestore/bid';
import { formatCurrency, formatTimestampCT } from '@util/index';
import { ProductDocument } from '@util/types/firestore/products';
import { useAuth } from 'context/AuthContext';
import React from 'react';
import { JamCrownIcon } from './AuctionStatus';

const Col = ({ text }: { text: string }) => (
  <div className="flex grow gap-[0.8rem] px-[1rem] py-[1.2rem]">
    <span
      className="text-[1.3rem] font-semibold text-[#4F4D4B]"
      suppressHydrationWarning
    >
      {text}
    </span>
  </div>
);

const THead = () => (
  <div className="flex w-full items-center border-t-[1px] border-[#eeeeee] bg-white">
    <Col text="Bidder" />
    <Col text="Amount" />
    <Col text="Time" />
  </div>
);

const BidCol = ({
  icon,
  text,
  leading,
}: {
  icon?: React.ReactNode | null;
  leading?: boolean;
  text: string;
}) => (
  <div className="flex w-full grow gap-[0.8rem] px-[1rem] py-[1.2rem]">
    {icon}
    <span
      className={`text-[1.3rem] font-semibold ${
        leading ? 'text-brand-secondary' : 'text-black'
      }`}
      suppressHydrationWarning
    >
      {text}
    </span>
  </div>
);
const TBid = ({
  bidder,
  amount,
  time,
  leading = false,
}: {
  leading?: boolean;
  bidder: string;
  amount: string;
  time: string;
}) => (
  <div
    className={`border-color-[#eeeeee] z-1 flex w-full items-center border-b-[1px]
    ${
      leading
        ? 'bg-brand-secondary-light'
        : bidder === 'You'
        ? 'bg-brand-primary-lighter'
        : 'bg-white'
    }
      `}
  >
    <BidCol
      icon={leading ? <JamCrownIcon /> : null}
      text={bidder}
      leading={leading}
    />
    <BidCol text={amount} />
    <BidCol text={time} />
  </div>
);

interface Props {
  bids: BidDocument[];
  product: ProductDocument;
}
export const BidHistoryTable = ({ bids, product }: Props) => {
  const { userDoc } = useAuth();

  const getUsername = (bid: BidDocument) => {
    if (bid.uid === userDoc?.uid) {
      return 'You';
    }
    const firstInitial = bid.username.charAt(0);
    const lastInitial = bid.username.charAt(bid.username.length - 1);
    return `${firstInitial}****${lastInitial}.`;
  };
  return (
    <div className="flex flex-col gap-[0.8rem]">
      <span className="text-[1.5rem] font-semibold">Bid History</span>
      <div className="isolate flex max-h-[30rem] flex-col">
        <THead />
        <div className="max-h-[30rem] overflow-y-auto">
          {bids.map((bid, i) => (
            <TBid
              key={bid.uid.slice(0, 5) + '-' + bid.bid_price + '-' + i}
              bidder={getUsername(bid)}
              amount={formatCurrency(bid.bid_price)}
              time={formatTimestampCT(bid.created)}
              leading={i === 0}
            />
          ))}
          <TBid
            key="starting-bid"
            bidder="Starting Bid"
            amount={formatCurrency(product.og_price)}
            time={formatTimestampCT(product.start_time ?? product.created)}
          />
        </div>
      </div>
    </div>
  );
};
